import * as Sentry from '@sentry/react';
import type { MouseEvent, ReactNode } from 'react';
import { useEffect, useMemo, useState } from 'react';

import {
  CheckmarkCircle,
  ChevronSmallDown,
  ChevronSmallUp,
  InformationCircle,
  WarningCircle,
  X,
} from '@remarkable/ark-icons';
import { Tag, Typography } from '@remarkable/ark-web';
import { cva } from 'class-variance-authority';
import clsx from 'clsx';
import { useAtom } from 'jotai';

import { HTTPError } from 'src/api/createApiClient.types';
import { IS_DEVELOPMENT } from 'src/config';
import { atomWithSessionStorage } from 'src/utils/atomWithSessionStorage';
import { cn } from 'src/utils/classNamesHelper';
import { UnreachableCodeError } from 'src/utils/unreachableCode';

import { Trans, useLingui } from '@lingui/react/macro';
import { Button } from './Button';

type NotificationVariants = 'error' | 'warning' | 'info' | 'success';

type NotificationError = Error | HTTPError;

type NotificationBoxBaseProps = {
  muted?: boolean;
  onClose?: (e: MouseEvent<HTMLButtonElement>) => void;
  children?: ReactNode;
  useErrorMessage?: boolean;
  className?: string;
  alwaysShow?: boolean;
  simple?: boolean;
};

type NotificationBoxManualProps = NotificationBoxBaseProps & {
  variant: NotificationVariants;
  title: string | ReactNode;
  error?: never;
};

type NotificationBoxWithErrorProps = NotificationBoxBaseProps & {
  variant?: NotificationVariants;
  title?: ReactNode;
  error:
    | NotificationError
    | null
    | undefined
    | (NotificationError | null | undefined)[];
};

type NotificationBoxProps =
  | NotificationBoxManualProps
  | NotificationBoxWithErrorProps;

const notificationBoxVariants = cva('flex rounded-sm md:flex-row', {
  variants: {
    variant: {
      error: 'bg-error-subtle [&>svg]:fill-feedback-red-500',
      warning: 'bg-[#FBEBD7]',
      info: 'bg-neutral-light-3',
      success: 'bg-[#E5EEE3]',
    },
    muted: {
      true: 'flex-row gap-8 bg-transparent',
      false: '',
    },
    simple: {
      true: 'px-12 py-2 items-start flex-row',
      false: '',
    },
    align: {
      left: 'md:items-start',
    },
  },
  compoundVariants: [
    {
      muted: false,
      simple: false,
      className: 'flex-col items-center p-16',
    },
  ],
  defaultVariants: {
    variant: 'info',
    muted: false,
    simple: false,
  },
});

const getIconElement = (variant: NotificationVariants) => {
  switch (variant) {
    case 'error':
      return WarningCircle;
    case 'warning':
    case 'info':
      return InformationCircle;
    case 'success':
      return CheckmarkCircle;
    default:
      throw new UnreachableCodeError(variant);
  }
};

export const showErrorBodyAtom = atomWithSessionStorage('showErrorBody', false);
const useShowErrorBody = () => useAtom(showErrorBodyAtom);

export const NotificationBox = ({
  title: titleProp,
  children,
  variant = 'error',
  muted = false,
  className = '',
  onClose,
  useErrorMessage = false,
  alwaysShow = false,
  simple = false,
  ...props
}: NotificationBoxProps) => {
  const { t } = useLingui();
  const title = titleProp ?? t`Something went wrong`;
  const align = children ? 'left' : null;
  const Icon = getIconElement(variant);

  const [showErrorBody, setShowErrorBody] = useShowErrorBody();

  const [responseBody, setResponseBody] = useState<unknown>();
  const [requestBody, setRequestBody] = useState<unknown>();

  const { error: notificationError, ...rest } = props;

  const error = Array.isArray(notificationError)
    ? notificationError.filter(Boolean)[0]
    : notificationError;

  useEffect(() => {
    if (error instanceof HTTPError) {
      Sentry.addBreadcrumb({
        type: 'error',
        category: 'ui.notification',
        message: `HTTPError: ${error.message}`,
        data: {
          title,
          status: error.response.status,
          statusText: error.response.statusText,
          method: error.request.method,
          url: error.request.url,
        },
      });
      if (!error.response.bodyUsed) {
        void error.response
          .clone()
          .text()
          .then((text) => {
            try {
              setResponseBody(JSON.parse(text));
            } catch {
              setResponseBody(text);
            }
          });
      }

      if (!error.request.bodyUsed) {
        void error.request
          .clone()
          .text()
          .then((text) => {
            try {
              setRequestBody(JSON.parse(text));
            } catch {
              setRequestBody(text);
            }
          });
      }
    } else if (error instanceof Error) {
      Sentry.addBreadcrumb({
        type: 'error',
        category: 'ui.notification',
        message: `Error: ${error.message}`,
        data: {
          title,
          name: error.name,
        },
      });
    } else {
      Sentry.addBreadcrumb({
        type: 'error',
        category: 'ui.notification',
        message: `NotificationBox rendered with non-error: ${String(error)}`,
        data: {
          title,
          error,
        },
      });
    }
  }, [error]);

  const requestUrl = useMemo(() => {
    if (!(error instanceof HTTPError)) {
      return null;
    }

    const url = new URL(error.request.url);

    return {
      path: url.pathname + url.search + url.hash,
      domain: url.origin,
    };
  }, [error]);

  if ('error' in props && !error && !alwaysShow) {
    // Error prop is in use, but no error has occured
    return null;
  }

  return (
    <div
      data-cy="notification-box"
      {...rest}
      className={notificationBoxVariants({
        variant,
        muted,
        align,
        simple,
        className,
      })}
    >
      {!simple && (
        <div>
          <Icon
            className={cn(muted ? 'h-[28px] w-[28px] px-2' : 'h-32 w-32', {
              'text-feedback-red-500': variant === 'error',
              'text-feedback-orange-500': variant === 'warning',
              'text-gray-800': variant === 'info',
              'text-feedback-green-500': variant === 'success',
            })}
          />
        </div>
      )}
      <div
        className={clsx(
          'flex w-full min-w-0 flex-col items-center text-center body-sm md:text-left',
          {
            'md:ml-16': !muted && !simple,
            'mt-2': muted && !simple,
            'md:items-start': !!children || error,
          }
        )}
      >
        <div
          className={clsx(
            'flex w-full items-center gap-16 md:justify-between',
            {
              'justify-center': !simple,
            }
          )}
        >
          <Typography
            variant="body-md"
            fontWeight={muted || simple ? 'regular' : 'bold'}
            className={muted || simple ? 'text-left' : ''}
            as="h2"
          >
            {title}
          </Typography>
          {onClose && (
            <Button
              data-cy="close-notification"
              variant="tertiary-neutral"
              onClick={onClose}
              size="small"
            >
              <X />
            </Button>
          )}
        </div>
        {children ??
          (!!props.error && (
            <span>
              {useErrorMessage && error instanceof Error ? (
                error.message
              ) : (
                <Trans>
                  Try again later or contact support if the problem persists.
                </Trans>
              )}
            </span>
          ))}

        {IS_DEVELOPMENT && error && (
          <div className="relative flex w-full justify-end sm:mt-0">
            <Button
              size="small"
              variant="tertiary-neutral"
              className="right-0 bottom-0 text-neutral-dark-1 sm:absolute"
              onClick={() => setShowErrorBody(!showErrorBody)}
            >
              {showErrorBody ? <ChevronSmallUp /> : <ChevronSmallDown />}{' '}
              <span>
                {showErrorBody ? <Trans>Hide</Trans> : <Trans>Details</Trans>}
              </span>
            </Button>
          </div>
        )}

        {IS_DEVELOPMENT && error && showErrorBody && (
          <div className="mt-16 w-full border-none bg-neutral-light-1/50 p-16 text-left">
            <Typography
              variant="interface-sm"
              fontWeight="medium"
              uppercase
              leadingNone
              className="muted"
            >
              {error.name}
            </Typography>
            <div>
              {error.message && (
                <Typography variant="interface-xs">{error.message}</Typography>
              )}
              {error instanceof HTTPError ? (
                <div className="flex flex-col gap-16">
                  <div>
                    <div className="flex justify-between gap-16 py-8">
                      <Typography
                        variant="interface-lg"
                        fontWeight="semibold"
                        leadingNone
                      >
                        <Trans context="network response">Response</Trans>
                      </Typography>
                      <Tag variant="themed-filled" className="bg-error-light">
                        <Trans context="network request status">Status</Trans>:{' '}
                        {error.response.status} {error.response.statusText}
                      </Tag>
                    </div>

                    <div className="overflow-x-scroll">
                      <Typography variant="interface-md">
                        <span className="text-muted">
                          {error.request.method}
                        </span>{' '}
                        {requestUrl?.path}
                      </Typography>
                      <Typography variant="interface-xs">
                        {requestUrl?.domain}
                      </Typography>
                    </div>

                    <div className="overflow-x-scroll">
                      <pre>{JSON.stringify(responseBody, null, 2)}</pre>
                    </div>
                  </div>
                  <div>
                    <Typography
                      variant="interface-lg"
                      fontWeight="semibold"
                      leadingNone
                      className="my-8"
                    >
                      <Trans context="network request">Request</Trans>
                    </Typography>

                    <Typography
                      variant="interface-sm"
                      fontWeight="medium"
                      uppercase
                      leadingNone
                    >
                      <Trans context="network request payload data">Body</Trans>
                    </Typography>

                    <pre>
                      {requestBody ? JSON.stringify(requestBody, null, 2) : '-'}
                    </pre>
                  </div>
                </div>
              ) : (
                <>
                  {error.cause && error.cause instanceof Error && (
                    <pre>
                      <Trans context="error reason">Cause</Trans>:{' '}
                      {error.cause.name}: {error.cause.message}
                    </pre>
                  )}
                  <pre className="overflow-scroll">{error.stack}</pre>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
